"use client";

import {
  Bold,
  Code,
  Italic,
  Link2,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { looksLikeMarkdown, markdownToHtml } from "./markdown";
import { htmlToComposerHtml, sanitizeComposerHtml } from "./serialize-html";

/**
 * Imperative handle the composer uses to drive the editor from outside React's
 * value flow (emoji insertion at the caret, clearing after send, focusing).
 */
export interface RichTextEditorHandle {
  focus(): void;
  clear(): void;
  /** Insert plain text at the caret (used by the emoji picker). */
  insertText(text: string): void;
}

interface RichTextEditorProps {
  /** Fired on every edit with the live HTML and its plain-text projection. */
  onChange: (value: { html: string; text: string }) => void;
  /** Enter (no Shift, and not inside a list) → send. */
  onEnter: () => void;
  /** Any input — drives the "visitor is typing" signal. */
  onTyping?: () => void;
  placeholder: string;
  ariaLabel: string;
  className?: string;
  /** Soft character cap — a paste beyond it is truncated (typing past it is
   *  left to the caller's counter + send guard). Omit for no cap. */
  maxChars?: number;
  /** HTML of a draft to restore on mount (re-entering a thread). Sanitised before it is used. */
  initialHtml?: string;
}

/** How far past `maxChars` a paste may go before we stop formatting it and
 *  drop in truncated PLAIN text instead. This is ONLY a freeze guard (a
 *  million-line paste would jank the contentEditable) — NOT the char limit,
 *  which the composer's counter + send guard enforce on visible text. Set well
 *  above the cap so a normal over-limit paste still converts to our styles. */
const PASTE_HARD_CAP_MULTIPLE = 10;

/** `document.execCommand` command names, keyed by our button ids. Deprecated
 *  but universally supported — the deliberate lightweight alternative to
 *  shipping a ProseMirror/Tiptap editor in a size-sensitive embed bundle. */
const COMMANDS = {
  bold: "bold",
  italic: "italic",
  underline: "underline",
  strike: "strikeThrough",
  bullet: "insertUnorderedList",
  ordered: "insertOrderedList",
} as const;

function queryState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

/** The active selection for the editor. The widget renders inside a Shadow
 *  DOM, where `document.getSelection()` doesn't reach the shadow tree in every
 *  browser — Chrome exposes `shadowRoot.getSelection()`, so prefer it. */
function editorSelection(el: HTMLElement | null): Selection | null {
  if (!el) return null;
  const root = el.getRootNode();
  if (root instanceof ShadowRoot) {
    const shadowSel = (root as ShadowRoot & { getSelection?: () => Selection | null }).getSelection;
    if (typeof shadowSel === "function") return shadowSel.call(root);
  }
  return document.getSelection();
}

/** The `<a>` the current selection sits inside, if any (and within `el`). */
function selectionLink(el: HTMLElement | null): HTMLAnchorElement | null {
  const node = editorSelection(el)?.anchorNode ?? null;
  const start = node instanceof HTMLElement ? node : node?.parentElement;
  const anchor = start?.closest?.("a") ?? null;
  return anchor && el?.contains(anchor) ? (anchor as HTMLAnchorElement) : null;
}

/** Escape text for safe interpolation into an `insertHTML` payload. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** The `<code>` the current selection sits inside, if any (and within `el`). */
function selectionCode(el: HTMLElement | null): HTMLElement | null {
  const node = editorSelection(el)?.anchorNode ?? null;
  const start = node instanceof HTMLElement ? node : node?.parentElement;
  const code = start?.closest?.("code") ?? null;
  return code && el?.contains(code) ? (code as HTMLElement) : null;
}

/**
 * Rich-text composer input: a `contentEditable` surface with a
 * formatting toolbar (bold · italic · underline · strike · bullet list ·
 * numbered list · link) that floats above the composer while text is selected.
 *
 * No editor library — commands run through `document.execCommand` and the
 * output is normalised/sanitised on send by `serialize-html.ts`. The toolbar
 * shows on a non-empty selection (Shadow-DOM aware) and swaps to a full-width
 * URL field in link mode.
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { onChange, onEnter, onTyping, placeholder, ariaLabel, className, maxChars, initialHtml },
    ref,
  ) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    // Last non-empty selection range, so the link field can re-apply to it
    // after focus moves to the URL input (which collapses the selection).
    const savedRangeRef = useRef<Range | null>(null);
    // True during an IME composition — input rules must not rewrite the DOM
    // mid-compose (it would abort the composition + drop characters).
    const composingRef = useRef(false);
    const [empty, setEmpty] = useState(true);
    const [selActive, setSelActive] = useState(false);
    const [marks, setMarks] = useState<Record<string, boolean>>({});
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const t = useTranslations("messages.format");

    // Restore a previous draft once, before the first paint, so there is no empty flash.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only restore
    useLayoutEffect(() => {
      const el = editorRef.current;
      if (!el || !initialHtml) return;
      const clean = sanitizeComposerHtml(initialHtml);
      if (!clean) return;
      el.innerHTML = clean;
      setEmpty(false);
    }, []);

    const emit = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      const text = el.innerText ?? el.textContent ?? "";
      setEmpty(text.trim().length === 0 && !el.querySelector("li"));
      onChange({ html: el.innerHTML, text });
    }, [onChange]);

    const refreshMarks = useCallback(() => {
      const onLink = !!selectionLink(editorRef.current);
      setMarks({
        bold: queryState(COMMANDS.bold),
        italic: queryState(COMMANDS.italic),
        // Links are underlined via CSS; don't let that light up the Underline
        // button (the visitor didn't apply an underline mark).
        underline: queryState(COMMANDS.underline) && !onLink,
        strike: queryState(COMMANDS.strike),
        code: !!selectionCode(editorRef.current),
        bullet: queryState(COMMANDS.bullet),
        ordered: queryState(COMMANDS.ordered),
        link: onLink,
      });
    }, []);

    // Show the toolbar whenever text is selected inside the editor; remember
    // the range so link-mode can restore it.
    const syncSelection = useCallback(() => {
      const el = editorRef.current;
      const sel = editorSelection(el);
      const active =
        !!el &&
        !!sel &&
        sel.rangeCount > 0 &&
        !sel.isCollapsed &&
        el.contains(sel.anchorNode) &&
        el.contains(sel.focusNode);
      setSelActive(active);
      if (active && sel) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        refreshMarks();
      }
    }, [refreshMarks]);

    useEffect(() => {
      const handler = () => syncSelection();
      document.addEventListener("selectionchange", handler);
      return () => document.removeEventListener("selectionchange", handler);
    }, [syncSelection]);

    const exec = useCallback(
      (command: string, value?: string) => {
        editorRef.current?.focus({ preventScroll: true });
        try {
          document.execCommand(command, false, value);
        } catch {
          /* unsupported command in this browser — no-op */
        }
        emit();
        refreshMarks();
      },
      [emit, refreshMarks],
    );

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          editorRef.current?.focus({ preventScroll: true });
        },
        clear() {
          if (editorRef.current) editorRef.current.innerHTML = "";
          setEmpty(true);
          setSelActive(false);
          setLinkOpen(false);
          setLinkUrl("");
          savedRangeRef.current = null;
        },
        insertText(text: string) {
          editorRef.current?.focus({ preventScroll: true });
          try {
            document.execCommand("insertText", false, text);
          } catch {
            /* no-op */
          }
          emit();
        },
      }),
      [emit],
    );

    // Monospace toggle — `execCommand` has no code command, but we STILL route
    // through it (`insertHTML` / `insertText`) rather than hand-editing the DOM
    // so the change lands on the browser's native undo stack. Hand-built DOM
    // mutations are invisible to Cmd+Z, which then corrupts/erases text (the
    // bug this fixes — bold worked because it's a native execCommand). On a
    // selection already inside a `<code>` it unwraps to plain text; otherwise
    // it wraps the highlighted text.
    const toggleMonospace = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      const sel = editorSelection(el);
      if (!sel) return;
      const existing = selectionCode(el);
      if (existing) {
        // Select the whole <code> and replace it with its plain text.
        const r = document.createRange();
        r.selectNode(existing);
        sel.removeAllRanges();
        sel.addRange(r);
        exec("insertText", existing.textContent ?? "");
        return;
      }
      if (sel.rangeCount === 0 || sel.isCollapsed) return;
      const selectedText = sel.toString();
      if (!selectedText) return;
      exec("insertHTML", `<code>${escapeHtml(selectedText)}</code>`);
    }, [exec]);

    // Inline markdown auto-format while typing ("input rules"). Runs after each
    // input on the caret's text node; only acts on a clear, completed pattern
    // and never corrupts content (any uncertainty → no-op). Skipped mid-IME.
    //   `*x*`  / `**x**`  → bold      (product spec: a single-star pair bolds)
    //   `` `x` ``          → monospace
    //   line-start `- ` / `* ` → bullet list ;  `1. ` → numbered list
    const applyInputRules = useCallback(() => {
      const el = editorRef.current;
      if (!el || composingRef.current) return;
      const sel = editorSelection(el);
      if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) return;
      const textNode = node as Text;
      const caret = range.startOffset;
      const text = textNode.textContent ?? "";
      const before = text.slice(0, caret);

      // ── Line-start list: the block's text up to the caret is JUST a marker.
      // Only when the text node starts the block (no prior sibling content).
      const atBlockStart = !textNode.previousSibling && caret === text.length;
      // `\s` (not an exact " ") so a browser-inserted &nbsp; after the marker
      // still matches — contentEditable turns a trailing space into  ,
      // which broke the `- ` bullet rule while `1. ` (also `\s`) worked.
      // Every mutation below goes through `exec` (execCommand) — NOT hand-built
      // DOM edits — so each lands on the browser's native undo stack and Cmd+Z
      // behaves like it does for the toolbar's bold/italic.
      if (atBlockStart && (/^[-*]\s$/.test(before) || /^\d+\.\s$/.test(before))) {
        const ordered = /^\d/.test(before);
        // Select + delete the marker (undoable), then toggle the list.
        const r = document.createRange();
        r.setStart(textNode, 0);
        r.setEnd(textNode, caret);
        sel.removeAllRanges();
        sel.addRange(r);
        exec("insertText", "");
        exec(ordered ? COMMANDS.ordered : COMMANDS.bullet);
        return;
      }

      // ── Wrap rules: a completed delimiter pair ending exactly at the caret.
      let m = /\*\*([^*\n]+?)\*\*$/.exec(before);
      let tag: "strong" | "code" = "strong";
      if (!m) m = /\*([^*\n]+?)\*$/.exec(before);
      if (!m) {
        const cm = /`([^`\n]+?)`$/.exec(before);
        if (cm) {
          m = cm;
          tag = "code";
        }
      }
      if (!m) return;
      const matchStart = caret - m[0].length;
      // Select the delimited run + replace it with the formatted element via
      // execCommand (undoable). execCommand places the caret after the insert.
      const r = document.createRange();
      r.setStart(textNode, matchStart);
      r.setEnd(textNode, caret);
      sel.removeAllRanges();
      sel.addRange(r);
      exec("insertHTML", `<${tag}>${escapeHtml(m[1])}</${tag}>`);
    }, [exec]);

    // Link button is a toggle: on an existing link it REMOVES the link;
    // otherwise it opens the URL field (the selection is already saved by
    // `syncSelection`, so it survives the field taking focus).
    const onLinkButton = useCallback(() => {
      if (selectionLink(editorRef.current)) {
        exec("unlink");
        return;
      }
      setLinkUrl("");
      setLinkOpen(true);
    }, [exec]);

    // Put the editor's selection back where it was before the URL field took
    // focus (so applying/cancelling doesn't lose the highlighted text), without
    // scrolling the caret into view.
    const restoreSelection = useCallback(() => {
      const el = editorRef.current;
      const range = savedRangeRef.current;
      if (!el) return null;
      el.focus({ preventScroll: true });
      const sel = editorSelection(el);
      if (sel && range) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      return sel;
    }, []);

    const applyLink = useCallback(() => {
      const url = linkUrl.trim();
      const el = editorRef.current;
      setLinkOpen(false);
      setLinkUrl("");
      if (!url || !el) return;
      const sel = restoreSelection();
      const range = savedRangeRef.current;
      if (sel && range && !range.collapsed) {
        exec("createLink", url);
      } else {
        // No selection to wrap → drop the URL in as its own linked label.
        const safe = url.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
        exec("insertHTML", `<a href="${safe}">${safe}</a>`);
      }
    }, [linkUrl, exec, restoreSelection]);

    const cancelLink = useCallback(() => {
      setLinkOpen(false);
      setLinkUrl("");
      // Keep the visitor's text selected so they can pick another action.
      restoreSelection();
    }, [restoreSelection]);

    const showToolbar = selActive || linkOpen;

    return (
      <div className={cn("relative", className)}>
        <AnimatePresence>
          {showToolbar && (
            <motion.div
              layout
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.96 }}
              transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                "absolute bottom-full left-2 z-20 mb-2 origin-bottom overflow-hidden",
                "rounded-2xl border border-wx-border/60 bg-wx-bg shadow-lg shadow-black/[0.06]",
              )}
              onMouseDown={(e) => e.preventDefault()}
            >
              <AnimatePresence mode="wait" initial={false}>
                {linkOpen ? (
                  <motion.div
                    key="link"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="flex items-center gap-1 p-1 pl-3"
                  >
                    <input
                      type="url"
                      data-wx-own-escape=""
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyLink();
                        } else if (e.key === "Escape") {
                          // Cancels the link field only: not the panel's Escape-to-close.
                          e.preventDefault();
                          e.stopPropagation();
                          cancelLink();
                        }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      placeholder={t("linkPlaceholder")}
                      aria-label={t("link")}
                      // biome-ignore lint/a11y/noAutofocus: focus follows the deliberate link action
                      autoFocus
                      className={cn(
                        "h-7 w-52 max-w-[52vw] bg-transparent text-[13px] text-wx-fg",
                        "placeholder:text-wx-fg-subtle focus:outline-none",
                      )}
                    />
                    <button
                      type="button"
                      onClick={applyLink}
                      className={cn(
                        "h-7 shrink-0 rounded-lg px-2.5 font-semibold text-[12px]",
                        "bg-wx-primary text-wx-primary-fg transition-[transform,opacity]",
                        "hover:opacity-95 active:scale-95",
                      )}
                    >
                      {t("linkSave")}
                    </button>
                    <ToolbarButton label={t("linkCancel")} onClick={cancelLink}>
                      <X className="h-4 w-4" />
                    </ToolbarButton>
                  </motion.div>
                ) : (
                  <motion.div
                    key="tools"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="flex items-center gap-0 p-1"
                  >
                    <ToolbarButton
                      label={t("bold")}
                      active={marks.bold}
                      onClick={() => exec(COMMANDS.bold)}
                    >
                      <Bold className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                      label={t("italic")}
                      active={marks.italic}
                      onClick={() => exec(COMMANDS.italic)}
                    >
                      <Italic className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                      label={t("underline")}
                      active={marks.underline}
                      onClick={() => exec(COMMANDS.underline)}
                    >
                      <Underline className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                      label={t("strikethrough")}
                      active={marks.strike}
                      onClick={() => exec(COMMANDS.strike)}
                    >
                      <Strikethrough className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                      label={t("monospace")}
                      active={marks.code}
                      onClick={toggleMonospace}
                    >
                      <Code className="h-4 w-4" />
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton
                      label={t("bulletList")}
                      active={marks.bullet}
                      onClick={() => exec(COMMANDS.bullet)}
                    >
                      <List className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                      label={t("numberedList")}
                      active={marks.ordered}
                      onClick={() => exec(COMMANDS.ordered)}
                    >
                      <ListOrdered className="h-4 w-4" />
                    </ToolbarButton>
                    <Divider />
                    <ToolbarButton label={t("link")} active={marks.link} onClick={onLinkButton}>
                      <Link2 className="h-4 w-4" />
                    </ToolbarButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative">
          {/* biome-ignore lint/a11y/useSemanticElements: a rich-text contentEditable can't be an <input>/<textarea> */}
          {/* biome-ignore lint/a11y/useFocusableInteractive: contentEditable is natively focusable — the linter can't see it */}
          <div
            ref={editorRef}
            role="textbox"
            aria-multiline="true"
            aria-label={ariaLabel}
            contentEditable
            suppressContentEditableWarning
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
            }}
            onInput={() => {
              applyInputRules();
              emit();
              onTyping?.();
            }}
            onPaste={(e) => {
              // Convert pasted content INTO our styles:
              //   1. rich HTML (from another site/editor) → our canonical subset
              //      (bold/italic/underline/strike/code/links/lists; headings →
              //      bold), so it matches the composer instead of foreign markup;
              //   2. else plain text that looks like Markdown → the same subset
              //      (so `**x**` / `- ` / `### ` land formatted, not as syntax);
              //   3. else plain text verbatim.
              e.preventDefault();
              const cd = e.clipboardData;
              const html = cd?.getData("text/html") ?? "";
              const text = cd?.getData("text/plain") ?? "";
              // Freeze guard ONLY — not the char limit. A truly enormous paste
              // (10x the cap) can't be formatted without janking the
              // contentEditable, so it goes in as TRUNCATED PLAIN text. A merely
              // over-the-cap paste still converts to our styles below: the char
              // limit is enforced by the composer's counter + send guard (which
              // count visible text), so formatting is never dropped just for
              // being long — the visitor trims it down and sends.
              if (maxChars) {
                const hardCap = maxChars * PASTE_HARD_CAP_MULTIPLE;
                const used = editorRef.current?.innerText?.length ?? 0;
                const remaining = Math.max(0, hardCap - used);
                if (text.length > remaining) {
                  if (remaining > 0) exec("insertText", text.slice(0, remaining));
                  return;
                }
              }
              if (html.trim()) {
                const converted = htmlToComposerHtml(html);
                if (converted) {
                  exec("insertHTML", converted);
                  return;
                }
              }
              if (text && looksLikeMarkdown(text)) {
                const converted = markdownToHtml(text);
                if (converted) {
                  exec("insertHTML", converted);
                  return;
                }
              }
              exec("insertText", text);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                // Enter that confirms an IME candidate belongs to the IME: it
                // must neither send nor be swallowed.
                if (e.nativeEvent.isComposing || composingRef.current) return;
                // Inside a list, Enter makes the next item (browser default);
                // a second Enter exits the list. Outside a list, Enter sends.
                if (queryState(COMMANDS.bullet) || queryState(COMMANDS.ordered)) {
                  requestAnimationFrame(emit);
                  return;
                }
                e.preventDefault();
                onEnter();
              }
            }}
            autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck={true}
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            className={cn(
              // No own `rounded` here: the composer CARD (parent) provides the
              // border + rounding. An inner scroll box with its OWN rounding
              // makes the scrollbar/content clash with the card's top border in
              // the corner (the "scroll eats the top border" artifact). Scroll
              // + max-height stay so a long draft caps + scrolls.
              "w-full overflow-y-auto bg-transparent px-4 pt-2.5 pb-1 text-sm text-wx-fg",
              "focus:outline-none",
              "[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5",
              // Monospace chip — gray rounded pill, like a code token.
              "[&_code]:rounded-wx-sm [&_code]:bg-wx-bg-elevated-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]",
              // Links read as links — coloured + underlined by default. The
              // Underline button's false-glow on a link is handled in
              // `refreshMarks` (it excludes link selections), not by dropping
              // the underline here.
              "[&_a]:font-medium [&_a]:text-wx-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-80",
            )}
            style={{ minHeight: 41, maxHeight: 120 }}
          />
          {empty && (
            <span
              className="pointer-events-none absolute top-2.5 left-4 select-none text-sm text-wx-fg-muted"
              aria-hidden="true"
            >
              {placeholder}
            </span>
          )}
        </div>
      </div>
    );
  },
);

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-wx-border/70" aria-hidden="true" />;
}

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-lg text-wx-fg-muted",
        "transition-[background-color,color,transform] hover:bg-wx-bg-elevated-2 hover:text-wx-fg active:scale-90",
        active && "bg-wx-primary/12 text-wx-primary hover:bg-wx-primary/12 hover:text-wx-primary",
      )}
    >
      {children}
    </button>
  );
}
